import React from "react";
import { motion } from "framer-motion";
import { MapPin, Calendar, CheckCircle2, Building2, ArrowUpRight } from "lucide-react";
import { EXECUTED_PROJECTS } from "../../data/hseData";

/* Photo tile that follows the cursor with a soft green spotlight */
const SpotlightPhoto = ({ src, alt, className = "", testid, aspect = "large" }) => {
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--mx", `${x}%`);
    e.currentTarget.style.setProperty("--my", `${y}%`);
  };
  return (
    <div
      data-testid={testid}
      onMouseMove={onMove}
      className={`photo-spotlight relative bg-[#05121F] group ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06] ${
          aspect === "large" ? "min-h-[420px]" : "min-h-[200px]"
        }`}
      />
      <div className="absolute inset-0 ring-1 ring-inset ring-white/10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#05121F]/60 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none" />
    </div>
  );
};

export const ExecutedProjects = () => {
  return (
    <section id="executados" data-testid="section-executados" className="relative py-24 sm:py-36 bg-[#081726]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Section head */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#2FA36B]" />
              <span className="font-condensed uppercase text-xs tracking-[0.4em] text-[#2FA36B] font-bold">
                Portfólio · Serviços Executados
              </span>
            </div>
            <h2 className="font-condensed font-bold uppercase text-white leading-[0.92] tracking-[-0.02em] text-4xl sm:text-6xl lg:text-7xl">
              Projetos entregues em
              <span className="text-[#2FA36B]"> operações críticas</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/65 font-body text-base leading-relaxed">
            Fotografia real dos serviços executados pela HSE. Acesso por cordas, tratamento de superfície e instalação em altura para clientes de diferentes segmentos.
          </p>
        </div>

        {/* Projects — editorial asymmetric layout */}
        <div className="space-y-24 sm:space-y-32">
          {EXECUTED_PROJECTS.map((p, i) => {
            const flipped = i % 2 === 1;
            return (
              <motion.article
                key={i}
                data-testid={`executed-project-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="grid grid-cols-12 gap-4 sm:gap-6 items-start"
              >
                {/* Photos block */}
                <div
                  className={`col-span-12 lg:col-span-7 grid grid-cols-6 gap-3 sm:gap-4 ${
                    flipped ? "lg:order-2" : ""
                  }`}
                >
                  {/* Hero photo - large clipped */}
                  <div className="col-span-6 sm:col-span-4">
                    <SpotlightPhoto
                      src={p.images[0]}
                      alt={`${p.client} — imagem principal`}
                      testid={`project-${i}-photo-0`}
                      className="aspect-[4/5]"
                    />
                  </div>
                  {/* Right column - two smaller frames */}
                  <div className="col-span-6 sm:col-span-2 flex flex-col gap-3 sm:gap-4">
                    {p.images.slice(1, 3).map((src, j) => (
                      <SpotlightPhoto
                        key={j}
                        src={src}
                        alt={`${p.client} — ${j + 2}`}
                        testid={`project-${i}-photo-${j + 1}`}
                        aspect="small"
                        className="aspect-square"
                      />
                    ))}
                  </div>
                </div>

                {/* Text block */}
                <div className={`col-span-12 lg:col-span-5 ${flipped ? "lg:order-1" : ""}`}>
                  <div className="lg:sticky lg:top-28">
                    <div className="flex items-center gap-3 mb-6 flex-wrap">
                      <span className="font-condensed font-bold text-[#2FA36B] tracking-[-0.03em] leading-none text-5xl sm:text-6xl">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className="h-px flex-1 bg-white/15" />
                      <span className="inline-flex items-center gap-1.5 font-condensed uppercase text-[11px] tracking-[0.25em] font-bold text-[#2FA36B] border border-[#2FA36B]/50 px-2.5 py-1">
                        <Calendar size={11} /> {p.year}
                      </span>
                    </div>

                    <div className="flex items-start gap-3 mb-4">
                      <Building2 size={16} className="text-[#2FA36B] mt-1 shrink-0" />
                      <div>
                        <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-white/40 font-bold">
                          Cliente
                        </p>
                        <p className="font-condensed font-bold uppercase text-base sm:text-lg text-white tracking-tight leading-tight">
                          {p.client}
                        </p>
                      </div>
                    </div>

                    <h3 className="font-condensed font-bold uppercase text-white leading-[0.98] tracking-[-0.01em] text-2xl sm:text-3xl lg:text-4xl mt-4 mb-5">
                      {p.title}
                    </h3>

                    <p className="font-body text-white/70 text-sm sm:text-base leading-relaxed mb-6 max-w-lg">
                      {p.scope}
                    </p>

                    <div className="flex items-center gap-2 mb-6 text-white/55">
                      <MapPin size={14} className="text-[#2FA36B]" />
                      <span className="font-condensed uppercase text-[11px] tracking-[0.25em]">
                        {p.location}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/10">
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1.5 font-condensed uppercase text-[10px] tracking-[0.25em] font-bold text-white/85 bg-white/[0.06] px-3 py-1.5"
                        >
                          <CheckCircle2 size={11} className="text-[#2FA36B]" /> {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA row */}
        <div className="mt-20 sm:mt-28 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/portfolio"
            data-testid="executed-portfolio-link"
            className="inline-flex items-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold border border-[#2FA36B] text-[#2FA36B] hover:bg-[#2FA36B] hover:text-white px-8 py-5 transition-colors"
          >
            Ver portfólio completo
            <ArrowUpRight size={16} />
          </a>
          <a
            href="#contato"
            data-testid="executed-cta"
            className="inline-flex items-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] text-white px-8 py-5 transition-all hover:-translate-y-0.5"
          >
            Quero um projeto assim para minha operação
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ExecutedProjects;
